/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { CalendarViews } from '../../enum/calendar-views.enum';
import { SimpleAction } from '../../tool/action/simple-action';
import { StandardAction } from '../../tool/action/standard-action';
import { EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Time } from '../../types/time.type';

if (dayjs) {
  dayjs.extend(isoWeek);
}

export class CalendarOptions {
  get orientation(): 'vertical' | 'horizontal' {
    return this._orientation;
  }

  set orientation(value: 'vertical' | 'horizontal') {
    this._orientation = value;
    this.changeView.emit({ v: this.view, o: this.orientation });
  }
  get view(): CalendarViews {
    return this._view;
  }

  set view(value: CalendarViews) {
    this._view = value;
    this.changeView.emit({ v: value, o: this._orientation });
  }

  private changeView = new EventEmitter<{
    v: CalendarViews;
    o: 'vertical' | 'horizontal';
  }>();

  public canNavigate = true;
  public canDrag = true;
  public canResize = true;
  public canSelect = true;
  public enable = true;
  public paginationStep = 5;
  public viewVerticalTotalTasksDuration = false;
  private _view: CalendarViews = CalendarViews.month;
  private _orientation: 'vertical' | 'horizontal' = 'vertical';
  public date = new Date();
  public actions: SimpleAction<any>[] = [
    new StandardAction('add', 'plus', 'calendar.addTask'),
    new StandardAction('paste', 'paste', 'calendar.pasteTask'),
  ];

  public taskActions: SimpleAction<any>[] = [
    new StandardAction('open', 'search', 'calendar.openTask'),
    new StandardAction('copy', 'copy', 'calendar.copyTask'),
    new StandardAction('delete', 'trash-alt', 'calendar.deleteTask'),
    new StandardAction('add', 'plus', 'calendar.addTask'),
  ];
  public month: {
    rowHeight: number;
    grayedCells: Date[];
  };

  public week: {
    dayOfWeek: {
      monday: boolean;
      tuesday: boolean;
      wednesday: boolean;
      thursday: boolean;
      friday: boolean;
      saturday: boolean;
      sunday: boolean;
    };
    horizontalWidthCell: 'auto' | number;
  };

  // Resources
  public resources: {
    start: Date;
    end: Date;
    title: string;
  };

  public day: {
    start: Time;
    endMorning: Time;
    startAfternoon: Time;
    end: Time;
    hoursSection: number;
    daySections: 1 | 2;
    alignTo: Time | 'currentHour';
    sectionHeight: number;
    hSectionHeight: number;
    defaultHSectionHeight: number;
    taskHeight: number; // Horizontal view
    defaultTaskHeight: number;
    heightCoefficient: number;
  };

  setDaySection(nbSection: 1 | 2) {
    this.day.hoursSection = null;
    this.day.daySections = nbSection;
  }

  setHourSection(nbSection: number) {
    this.day.daySections = null;
    this.day.hoursSection = nbSection;
  }

  constructor() {
    this.actions[1].enable = false;
    this.month = {
      rowHeight: 100,
      grayedCells: [],
    };
    this.day = {
      start: { hours: 7, minutes: 0 },
      endMorning: { hours: 12, minutes: 0 },
      startAfternoon: { hours: 13, minutes: 0 },
      end: { hours: 18, minutes: 0 },
      hoursSection: 2,
      daySections: 1,
      alignTo: null,
      sectionHeight: 30,
      hSectionHeight: 30,
      defaultHSectionHeight: 30,
      taskHeight: 30,
      defaultTaskHeight: 30,
      heightCoefficient: 1
    };
    this.week = {
      dayOfWeek: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
      horizontalWidthCell: 'auto',
    };
    this.resources = {
      start: this.generateDateStart(),
      end: this.generateDateEnd(),
      title: null,
    };
  }

  generateDateStart(): Date {
    switch (this._view) {
      case CalendarViews.day:
        return this.date;
      case CalendarViews.week:
      case CalendarViews.fortnight:
        return dayjs(this.date).startOf('isoWeek').toDate();
      case CalendarViews.month:
      case CalendarViews.resources:
      case CalendarViews.resourcesDetailed:
        return dayjs(this.date).startOf('month').toDate();
    }
  }

  generateDateEnd(): Date {
    switch (this._view) {
      case CalendarViews.day:
        return this.date;
      case CalendarViews.week:
        return dayjs(this.date).endOf('isoWeek').toDate();
      case CalendarViews.fortnight:
        return dayjs(this.date).add(1, 'week').endOf('isoWeek').toDate();
      case CalendarViews.month:
        return dayjs(this.date).endOf('month').toDate();
      case CalendarViews.resources:
      case CalendarViews.resourcesDetailed:
        return dayjs(this.date).add(1, 'month').endOf('month').toDate();
    }
  }

  public getChangeView(): Observable<{
    v: CalendarViews;
    o: 'vertical' | 'horizontal';
  }> {
    return this.changeView.asObservable();
  }

  public computeOffsetTop(): number {
    if (this.day.alignTo) {
      let time: Time;
      if (this.day.alignTo === 'currentHour') {
        const d = new Date();
        time = { hours: d.getHours(), minutes: d.getMinutes() };
      } else {
        time = this.day.alignTo;
      }
      return this.getOffsetTop(time);
    } else {
      return 0;
    }
  }

  private getOffsetTop(time: Time): number {
    const mStart = this.day.start.hours * 60 + this.day.start.minutes;
    const mOffset = time.hours * 60 + time.minutes;
    const sectionIndex = (mOffset - mStart) / 60;
    return sectionIndex * (this.day.sectionHeight * this.day.hoursSection);
  }
}
